import { isSupabaseConfigured } from "@/lib/env";
import { publicSupabase } from "@/lib/supabase";
import type { Json } from "@/lib/database.types";
import type {
  BillingFeatureCopy,
  BillingOffer,
  BillingOfferCode,
  BillingSubscriptionOffer,
  BillingTopUpOffer,
} from "@/features/billing/billing.types";
import type { SubscriptionTier } from "@/types/projects";

export type PricingServiceKey =
  | "ai_chat"
  | "continuation_suggestion"
  | "continuity_review"
  | "structural_analysis"
  | "image_generation"
  | "story_import"
  | "timeline_summary"
  | "book_assembly_placement"
  | "enhance_selection"
  | "polish_chapter";

export interface RuntimeServicePricing {
  analysisTokenMultiplier: number | null;
  creditCost: number;
  minimumCreditCost: number;
  serviceKey: PricingServiceKey;
  tokensPerCredit: number | null;
  tokensPerWord: number | null;
}

export interface RuntimePricingCatalog {
  billingOffers: BillingOffer[];
  servicePricing: Record<PricingServiceKey, RuntimeServicePricing>;
}

type BillingSubscriptionOfferSeed = Omit<BillingSubscriptionOffer, "features">;
type BillingTopUpOfferSeed = Omit<BillingTopUpOffer, "features">;
type BillingOfferSeed = BillingSubscriptionOfferSeed | BillingTopUpOfferSeed;

const defaultBillingOfferSeeds: BillingOfferSeed[] = [
  {
    billingInterval: "monthly",
    currencyCode: "USD",
    kind: "subscription",
    monthlyCredits: 500,
    offerCode: "crafter_monthly",
    priceCents: 1400,
    sortOrder: 10,
    tier: "crafter",
  },
  {
    billingInterval: "annual",
    currencyCode: "USD",
    kind: "subscription",
    monthlyCredits: 500,
    offerCode: "crafter_annual",
    priceCents: 12000,
    sortOrder: 20,
    tier: "crafter",
  },
  {
    billingInterval: "monthly",
    currencyCode: "USD",
    kind: "subscription",
    monthlyCredits: 1500,
    offerCode: "author_monthly",
    priceCents: 2800,
    sortOrder: 30,
    tier: "author",
  },
  {
    billingInterval: "annual",
    currencyCode: "USD",
    kind: "subscription",
    monthlyCredits: 1500,
    offerCode: "author_annual",
    priceCents: 24000,
    sortOrder: 40,
    tier: "author",
  },
  {
    creditAmount: 500,
    currencyCode: "USD",
    kind: "top_up",
    offerCode: "credits_500",
    priceCents: 1000,
    sortOrder: 110,
  },
  {
    creditAmount: 1000,
    currencyCode: "USD",
    kind: "top_up",
    offerCode: "credits_1000",
    priceCents: 1800,
    sortOrder: 120,
  },
  {
    creditAmount: 2000,
    currencyCode: "USD",
    kind: "top_up",
    offerCode: "credits_2000",
    priceCents: 3200,
    sortOrder: 130,
  },
];

const defaultServicePricingEntries: RuntimeServicePricing[] = [
  {
    analysisTokenMultiplier: null,
    creditCost: 1,
    minimumCreditCost: 1,
    serviceKey: "ai_chat",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
  {
    analysisTokenMultiplier: null,
    creditCost: 2,
    minimumCreditCost: 2,
    serviceKey: "continuation_suggestion",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
  {
    analysisTokenMultiplier: null,
    creditCost: 3,
    minimumCreditCost: 3,
    serviceKey: "continuity_review",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
  {
    analysisTokenMultiplier: null,
    creditCost: 10,
    minimumCreditCost: 10,
    serviceKey: "structural_analysis",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
  {
    analysisTokenMultiplier: null,
    creditCost: 5,
    minimumCreditCost: 5,
    serviceKey: "image_generation",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
  {
    analysisTokenMultiplier: 7,
    creditCost: 25,
    minimumCreditCost: 25,
    serviceKey: "story_import",
    tokensPerCredit: 5000,
    tokensPerWord: 1.42,
  },
  {
    analysisTokenMultiplier: null,
    creditCost: 1,
    minimumCreditCost: 1,
    serviceKey: "timeline_summary",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
  {
    analysisTokenMultiplier: null,
    creditCost: 3,
    minimumCreditCost: 3,
    serviceKey: "book_assembly_placement",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
  {
    analysisTokenMultiplier: null,
    creditCost: 2,
    minimumCreditCost: 2,
    serviceKey: "enhance_selection",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
  {
    analysisTokenMultiplier: null,
    creditCost: 3,
    minimumCreditCost: 3,
    serviceKey: "polish_chapter",
    tokensPerCredit: null,
    tokensPerWord: null,
  },
];

let runtimePricingCatalogCache: RuntimePricingCatalog = buildRuntimePricingCatalog(
  defaultBillingOfferSeeds,
  defaultServicePricingEntries,
);
let runtimePricingCatalogPromise: Promise<RuntimePricingCatalog> | null = null;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isPricingServiceKey(value: unknown): value is PricingServiceKey {
  return value === "ai_chat"
    || value === "continuation_suggestion"
    || value === "continuity_review"
    || value === "structural_analysis"
    || value === "image_generation"
    || value === "story_import"
    || value === "timeline_summary"
    || value === "book_assembly_placement"
    || value === "enhance_selection"
    || value === "polish_chapter";
}

function isSubscriptionTier(value: unknown): value is Exclude<SubscriptionTier, "free"> {
  return value === "crafter" || value === "author";
}

function toPositiveInteger(value: unknown, fallback: number) {
  const normalized = typeof value === "number" ? value : Number(value);
  return Number.isFinite(normalized) && normalized >= 0 ? Math.trunc(normalized) : fallback;
}

function toPositiveNumber(value: unknown, fallback: number | null) {
  const normalized = typeof value === "number" ? value : Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : fallback;
}

function buildBillingFeatureCopy(seed: BillingOfferSeed): BillingFeatureCopy[] {
  if (seed.kind === "top_up") {
    return [
      {
        descriptionKey: "billing.topUps.dynamicDescription",
        labelKey: "billing.topUps.dynamicTitle",
        labelValues: {
          count: seed.creditAmount,
        },
      },
    ];
  }

  const tierKey = seed.tier === "author" ? "author" : "crafter";

  return [
    {
      descriptionKey: `billing.plans.${tierKey}.features.projects`,
      labelKey: `billing.plans.${tierKey}.features.projectsLabel`,
    },
    {
      descriptionKey: `billing.plans.${tierKey}.features.ai`,
      labelKey: `billing.plans.${tierKey}.features.aiLabel`,
    },
    {
      descriptionKey: `billing.plans.${tierKey}.features.credits`,
      labelKey: "billing.plans.features.creditsLabelDynamic",
      labelValues: {
        count: seed.monthlyCredits,
      },
    },
  ];
}

function decorateBillingOffer(seed: BillingOfferSeed): BillingOffer {
  return {
    ...seed,
    features: buildBillingFeatureCopy(seed),
  };
}

function buildServicePricingRecord(entries: RuntimeServicePricing[]) {
  return entries.reduce<Record<PricingServiceKey, RuntimeServicePricing>>((accumulator, entry) => {
    accumulator[entry.serviceKey] = entry;
    return accumulator;
  }, {} as Record<PricingServiceKey, RuntimeServicePricing>);
}

function buildRuntimePricingCatalog(
  billingOfferSeeds: BillingOfferSeed[],
  servicePricingEntries: RuntimeServicePricing[],
): RuntimePricingCatalog {
  return {
    billingOffers: billingOfferSeeds
      .map(decorateBillingOffer)
      .sort((left, right) => left.sortOrder - right.sortOrder || left.offerCode.localeCompare(right.offerCode)),
    servicePricing: buildServicePricingRecord(servicePricingEntries),
  };
}

function normalizeBillingOffer(raw: unknown): BillingOfferSeed | null {
  if (!isPlainObject(raw)) {
    return null;
  }

  const offerCode = typeof raw.offerCode === "string" && raw.offerCode.trim().length > 0
    ? raw.offerCode.trim()
    : null;
  const kind = raw.kind === "subscription" || raw.kind === "top_up" ? raw.kind : null;
  const priceCents = toPositiveInteger(raw.priceCents, -1);
  const currencyCode = typeof raw.currencyCode === "string" && raw.currencyCode.trim().length > 0
    ? raw.currencyCode.trim().toUpperCase()
    : "USD";

  if (!offerCode || !kind || priceCents < 0) {
    return null;
  }

  if (kind === "subscription") {
    const tier = isSubscriptionTier(raw.tier) ? raw.tier : null;
    const billingInterval = raw.billingInterval === "monthly" || raw.billingInterval === "annual"
      ? raw.billingInterval
      : null;

    if (!tier || !billingInterval) {
      return null;
    }

    return {
      billingInterval,
      currencyCode,
      kind,
      monthlyCredits: toPositiveInteger(raw.monthlyCredits, 0),
      offerCode,
      priceCents,
      sortOrder: toPositiveInteger(raw.sortOrder, 0),
      tier,
    };
  }

  const creditAmount = toPositiveInteger(raw.creditAmount, -1);
  if (creditAmount <= 0) {
    return null;
  }

  return {
    creditAmount,
    currencyCode,
    kind,
    offerCode,
    priceCents,
    sortOrder: toPositiveInteger(raw.sortOrder, 0),
  };
}

function normalizeServicePricingEntry(raw: unknown): RuntimeServicePricing | null {
  if (!isPlainObject(raw) || !isPricingServiceKey(raw.serviceKey)) {
    return null;
  }

  return {
    analysisTokenMultiplier: toPositiveNumber(raw.analysisTokenMultiplier, null),
    creditCost: toPositiveInteger(raw.creditCost, 0),
    minimumCreditCost: toPositiveInteger(raw.minimumCreditCost, 0),
    serviceKey: raw.serviceKey,
    tokensPerCredit: toPositiveInteger(raw.tokensPerCredit, 0) || null,
    tokensPerWord: toPositiveNumber(raw.tokensPerWord, null),
  };
}

function normalizeRuntimePricingCatalogPayload(payload: Json | null): RuntimePricingCatalog {
  if (!isPlainObject(payload)) {
    return runtimePricingCatalogCache;
  }

  const billingOfferSeeds = Array.isArray(payload.billingOffers)
    ? payload.billingOffers.map(normalizeBillingOffer).filter((value): value is BillingOfferSeed => value !== null)
    : [];
  const servicePricingEntries = Array.isArray(payload.servicePricing)
    ? payload.servicePricing
      .map(normalizeServicePricingEntry)
      .filter((value): value is RuntimeServicePricing => value !== null)
    : [];

  return buildRuntimePricingCatalog(
    billingOfferSeeds.length > 0 ? billingOfferSeeds : defaultBillingOfferSeeds,
    servicePricingEntries.length > 0 ? servicePricingEntries : defaultServicePricingEntries,
  );
}

export const defaultRuntimePricingCatalog = runtimePricingCatalogCache;

export function getRuntimePricingCatalogSnapshot() {
  return runtimePricingCatalogCache;
}

export async function fetchRuntimePricingCatalog(options: { force?: boolean } = {}) {
  if (!isSupabaseConfigured()) {
    return runtimePricingCatalogCache;
  }

  if (!options.force && runtimePricingCatalogPromise) {
    return runtimePricingCatalogPromise;
  }

  runtimePricingCatalogPromise = (async () => {
    try {
      const { data, error } = await publicSupabase.rpc("get_public_pricing_catalog");
      if (error) {
        throw error;
      }

      runtimePricingCatalogCache = normalizeRuntimePricingCatalogPayload(data ?? null);
      return runtimePricingCatalogCache;
    } catch {
      return runtimePricingCatalogCache;
    } finally {
      runtimePricingCatalogPromise = null;
    }
  })();

  return runtimePricingCatalogPromise;
}

export function getRuntimeBillingOffer(
  offerCode: BillingOfferCode | null | undefined,
  catalog: RuntimePricingCatalog = runtimePricingCatalogCache,
) {
  return catalog.billingOffers.find((offer) => offer.offerCode === offerCode) ?? null;
}

export function getRuntimeTopUpOffers(catalog: RuntimePricingCatalog = runtimePricingCatalogCache) {
  return catalog.billingOffers.filter((offer) => offer.kind === "top_up");
}

export function getDefaultTopUpOfferCode(catalog: RuntimePricingCatalog = runtimePricingCatalogCache) {
  return getRuntimeTopUpOffers(catalog)[0]?.offerCode ?? "credits_500";
}

export function getRuntimeServicePricing(
  serviceKey: PricingServiceKey,
  catalog: RuntimePricingCatalog = runtimePricingCatalogCache,
) {
  return catalog.servicePricing[serviceKey] ?? defaultRuntimePricingCatalog.servicePricing[serviceKey];
}

export function formatCurrencyAmount(
  amountInCents: number,
  currencyCode: string,
  locale = "en",
) {
  return new Intl.NumberFormat(locale, {
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
    style: "currency",
  }).format(amountInCents / 100);
}
